// TimelineDayView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    buildTopHeaderSegments,
    DAY_MS,
    formatTimelineRangeText,
    getInclusiveEndOffsetMs,
    getScheduledBounds,
    getScheduledRows,
    getTimelineBarStyle,
    resolveViewportTimelineBounds,
} from '../shared/timelineViewUtils';

// CSS
import './TimelineDayView.css';

const DAY_COLUMN_WIDTH = 42;

const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const endOfDay = (date) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
};

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const addMonths = (date, months) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const startOfMonth = (date) => {
    const next = startOfDay(date);
    next.setDate(1);
    return next;
};

const endOfMonth = (date) => {
    const firstOfNextMonth = startOfMonth(addDays(date, 32));
    return endOfDay(addDays(firstOfNextMonth, -1));
};

/**
 * TimelineDayView
 *
 * Right-side timeline renderer for day-scale timeline.
 *
 * Top row: Month (e.g., March 2026)
 * Bottom row: Day numbers (1 | 2 | 3 ...)
 *
 * @returns {ReactNode} The rendered component.
 */
const TimelineDayView = React.memo(({
    timelineRows = [],
    timelineFieldDef,
    viewportBounds = null,
    onNodeClick,
    onNodeDoubleClick,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [loadedTrailingMonths, setLoadedTrailingMonths] = useState(0);
    // ----- REFS ----- //

    const scrollRef = useRef(null);
    const lastLoadTriggerWidthRef = useRef(0);

    // ----- MEMOS ----- //

    const scheduledRows = useMemo(() => getScheduledRows(timelineRows), [timelineRows]);

    const scheduledSpanResetToken = useMemo(() => {
        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return `none_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
        return `${scheduledBounds.minStart.getTime()}_${scheduledBounds.maxEnd.getTime()}_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
    }, [scheduledRows, timelineFieldDef?.key, timelineFieldDef?.name]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setLoadedTrailingMonths(0);
        lastLoadTriggerWidthRef.current = 0;
    }, [scheduledSpanResetToken]);

    // ----- MEMOS ----- //

    const bounds = useMemo(() => {
        const viewport = resolveViewportTimelineBounds(viewportBounds);
        if (viewport) return viewport;

        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return null;

        const start = startOfMonth(scheduledBounds.minStart);
        const baseEndMonth = startOfMonth(scheduledBounds.maxEnd);
        const endMonth = loadedTrailingMonths > 0
            ? startOfMonth(addMonths(baseEndMonth, loadedTrailingMonths))
            : baseEndMonth;
        const end = endOfMonth(endMonth);
        return { start, end };
    }, [scheduledRows, loadedTrailingMonths, viewportBounds]);

    // ----- HANDLERS ----- //

    const handleScroll = useCallback(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const nearRightEdge = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 48;
        if (!nearRightEdge) return;

        if (lastLoadTriggerWidthRef.current === scrollEl.scrollWidth) return;
        lastLoadTriggerWidthRef.current = scrollEl.scrollWidth;
        setLoadedTrailingMonths((prev) => prev + 1);
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => handleScroll();
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [handleScroll]);

    // ----- MEMOS ----- //

    const columns = useMemo(() => {
        if (!bounds) return [];

        const out = [];
        let cursor = startOfDay(bounds.start);

        while (cursor.getTime() <= bounds.end.getTime()) {
            const dayStart = new Date(cursor);
            const next = addDays(cursor, 1);
            const dayEnd = new Date(next.getTime() - 1);
            out.push({
                key: `d_${dayStart.getTime()}`,
                start: dayStart,
                end: dayEnd,
                topLabel: dayStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                bottomLabel: dayStart.toLocaleDateString('en-US', { day: 'numeric' }),
            });
            cursor = next;
        }

        return out;
    }, [bounds]);

    const topHeaderSegments = useMemo(
        () => buildTopHeaderSegments(columns, { labelKey: 'topLabel', keyPrefix: 'm' }),
        [columns]
    );

    const trackWidth = useMemo(() => (
        Math.max(720, (columns.length || 1) * DAY_COLUMN_WIDTH)
    ), [columns.length]);

    const spanMs = useMemo(() => {
        if (!bounds) return 0;
        return Math.max(1, bounds.end.getTime() - bounds.start.getTime());
    }, [bounds]);

    if (!bounds || columns.length === 0) {

        return (
            <div className="timeline-day-view-container">

                {/* EMPTY */}
                <div
                  className="timeline-day-view-empty"
                >
                    No scheduled timeline items yet.
                </div>
            </div>
        );
    }
    // ----- RENDER ----- //

    return (
        <div
          className="timeline-day-view-container"
          style={{
                '--timeline-day-column-width': `${DAY_COLUMN_WIDTH}px`,
            }}
        >

            {/* SCROLL */}
            <div className="timeline-day-view-scroll" ref={scrollRef}>

                {/* TIMELINE DAY HEADER */}
                <div
                  className="timeline-day-header"
                  style={{
                        borderBottom: `1px solid ${theme.status}`,
                        width: `${trackWidth}px`,
                    }}
                >

                    {/* TIMELINE DAY HEADER ROW TOP */}
                    <div
                      className="timeline-day-header-row timeline-day-header-row-top"
                      style={{
                        borderBottom: `1px solid ${theme.status}`,
                      }}
                    >
                        {topHeaderSegments.map((segment) => (
                            <div
                              className="timeline-day-header-cell timeline-day-header-cell-top"
                              style={{
                                minWidth: `${segment.span * DAY_COLUMN_WIDTH}px`,
                              }}
                              key={segment.key}
                              title={segment.label}
                            >
                                {segment.label}
                            </div>
                        ))}
                    </div>

                    {/* TIMELINE DAY HEADER ROW BOTTOM */}
                    <div
                      className="timeline-day-header-row timeline-day-header-row-bottom"
                      style={{
                            backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${DAY_COLUMN_WIDTH}px)`,
                        }}
                    >
                        {columns.map((column) => (
                            <div
                              className="timeline-day-header-cell timeline-day-header-cell-bottom"
                              style={{
                                minWidth: `${DAY_COLUMN_WIDTH}px`,
                              }}
                              key={column.key}
                              title={column.bottomLabel}
                            >
                                {column.bottomLabel}
                            </div>
                        ))}
                    </div>
                </div>

                {/* TIMELINE DAY BODY */}
                <div
                  className="timeline-day-body"
                  style={{
                    width: `${trackWidth}px`,
                  }}
                >
                    {timelineRows.map((row, rowIndex) => {
                        const rowDividerStyle = rowIndex > 0
                            ? { borderTop: `1px solid ${theme.status}` }
                            : undefined;

                        if (row.type === 'group-header') {
                            return (
                                <div
                                  className="timeline-day-row timeline-day-row-group"
                                  style={{
                                    ...rowDividerStyle,
                                  }}
                                  key={row.key}
                                />
                            );
                        }

                        const entry = row.entry;
                        const isUnscheduled = row.type === 'item-unscheduled';
                        const barStyle = getTimelineBarStyle({
                            entry,
                            bounds,
                            spanMs,
                            trackWidth,
                            minDurationMs: DAY_MS,
                            minWidthPercent: 1.1,
                            inclusiveEndMs: getInclusiveEndOffsetMs(timelineFieldDef?.type),
                        });
                        const rangeText = formatTimelineRangeText(entry?.range, timelineFieldDef?.type);
                        const showBarLabel = barStyle?.widthPx >= 120;

                        return (
                            <div
                              className="timeline-day-row timeline-day-row-item"
                              style={{
                                    ...rowDividerStyle,
                                    backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${DAY_COLUMN_WIDTH}px)`,
                                }}
                              key={row.key}
                              onClick={(event) => onNodeClick?.(event, entry?.node)}
                              onDoubleClick={(event) => onNodeDoubleClick?.(event, entry?.node)}
                            >
                                {!isUnscheduled && barStyle && (
                                    <div
                                      className={`timeline-day-bar${entry?.range?.isMilestone ? ' is-milestone' : ''}`}
                                      style={{
                                            backgroundColor: entry?.barColor || theme.highlight,
                                            color: entry?.barForeground || undefined,
                                            left: barStyle.left,
                                            width: barStyle.width,
                                        }}
                                      title={rangeText}
                                    >

                                        {/* TIMELINE DAY BAR LABEL */}
                                        {showBarLabel && (
                                            <span
                                              className="timeline-day-bar-label"
                                            >{rangeText}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

// Dev Tools
TimelineDayView.displayName = 'TimelineDayView';

export default TimelineDayView;
